import { ConnectAccountModal } from '@components/ConnectAccountModal/ConnectAccountModal';
import { MultipleToggle } from '@components/MultipleToggle/MultipleToggle';
import { useIsMobile } from '@lib/hooks/sizing/useIsMobile';
import {
  calendarGrid,
  formatMonthParam,
  parseMonthParam,
  rangeKey,
} from '@lib/social/scheduling/window';
import { AddRounded } from '@mui/icons-material';
import { Tooltip } from '@mui/material';
import { ActionButton } from '@pages/ActionButton';
import { AppPage } from '@pages/AppPage';
import { scheduleSettings } from '@stores/ScheduleSettings';
import { socialConnectionsStore } from '@stores/SocialConnections';
import { socialScheduleStore } from '@stores/SocialSchedule';
import { trackingStore } from '@stores/Tracking';
import { isSameMonth } from 'date-fns';
import { observer } from 'mobx-react-lite';
import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { MonthDirection, MonthNav } from './components/MonthNav/MonthNav';
import { ScheduleAgenda } from './components/ScheduleAgenda/ScheduleAgenda';
import { ScheduleCalendar } from './components/ScheduleCalendar/ScheduleCalendar';
import { TimeZoneButton } from './components/TimeZoneDialog/TimeZoneButton';
import css from './SchedulePage.module.scss';

type ScheduleView = 'calendar' | 'list';

const VIEW_PARAM = 'view';
const MONTH_PARAM = 'month';
const DEFAULT_VIEW: ScheduleView = 'calendar';
const VIEW_OPTIONS: { value: ScheduleView; label: string }[] = [
  { value: 'calendar', label: 'Calendar' },
  { value: 'list', label: 'List' },
];

const parseView = (value: string | null): ScheduleView =>
  value === 'list' ? 'list' : DEFAULT_VIEW;

/**
 * What is scheduled and what became of it, as a month calendar or an agenda
 * list. The URL owns the view and the month, so back, refresh and a shared
 * link all land on the same screen.
 */
export const SchedulePage = observer(function SchedulePage() {
  const isMobile = useIsMobile();
  const [searchParams, setSearchParams] = useSearchParams();
  const [connectOpen, setConnectOpen] = useState(false);
  // No calendar on a phone: seven columns of chips do not fit. The param is
  // left alone so widening the window brings the calendar back.
  const view: ScheduleView = isMobile ? 'list' : parseView(searchParams.get(VIEW_PARAM));

  // Fixed at mount: "this month" and the today ring do not move under the
  // user if the page stays open across midnight. Read in the schedule's zone,
  // so the month, the grid and the ring follow a zone change at once.
  const [nowMs] = useState(Date.now);
  const now = scheduleSettings.inZone(nowMs);
  const month = parseMonthParam(searchParams.get(MONTH_PARAM), now);
  // One request per month serves both views: the grid holds the month. The
  // range is in ms, so a zone change is a new grid and a new request.
  const grid = calendarGrid(month);
  const gridKey = rangeKey(grid.range);

  useEffect(() => {
    socialConnectionsStore.update().catch(() => undefined);
    socialScheduleStore.startRefreshing();
    return () => socialScheduleStore.stopRefreshing();
  }, []);

  useEffect(() => {
    void socialScheduleStore.load(grid.range);
    // The range is a pure function of its key.
  }, [gridKey]);

  const setView = (next: ScheduleView) => {
    trackingStore.scheduleViewChanged({ view: next });
    setSearchParams(
      (params) => {
        if (next === DEFAULT_VIEW) params.delete(VIEW_PARAM);
        else params.set(VIEW_PARAM, next);
        return params;
      },
      { replace: true },
    );
  };

  const goToMonth = (next: Date, direction: MonthDirection) => {
    trackingStore.scheduleMonthChanged({ direction });
    setSearchParams(
      (params) => {
        if (isSameMonth(next, now)) params.delete(MONTH_PARAM);
        else params.set(MONTH_PARAM, formatMonthParam(next));
        return params;
      },
      { replace: true },
    );
  };

  // "Loaded and empty", not "absent": a failed connections list is not a
  // reason to tell the user they have no accounts.
  const connections = socialConnectionsStore.data;
  const noAccounts = connections !== null && connections.length === 0;

  const controls = (
    <div className={css.controls}>
      {!isMobile && <MultipleToggle options={VIEW_OPTIONS} value={view} onChange={setView} />}
      <MonthNav month={month} now={now} onChange={goToMonth} />
      <TimeZoneButton now={now} />
    </div>
  );
  const actions = (
    <div className={css.actions}>
      <ActionButton variant="secondary" onClick={() => setConnectOpen(true)}>
        Connect account
      </ActionButton>
      {/* FIXME(publishing-render-list): the compose page exists (/schedule/create/:renderId) but
          there is no list of renders to pick from yet, so the button stays disabled. */}
      <Tooltip title="Coming soon">
        <span className={css.newPostWrap}>
          <ActionButton variant="primary" disabled>
            <AddRounded />
            &nbsp;New post
          </ActionButton>
        </span>
      </Tooltip>
    </div>
  );

  return (
    <AppPage header="Schedule">
      <div className={css.container}>
        {/* On a phone the actions sit above the controls, in the DOM as on screen, so the tab order follows the eye. */}
        <div className={css.headerStrip}>
          {isMobile ? actions : controls}
          {isMobile ? controls : actions}
        </div>
        <ConnectAccountModal
          open={connectOpen}
          onClose={() => setConnectOpen(false)}
          source="schedule"
        />

        <div className={css.scrollableContent}>
          {noAccounts && (
            <div className={css.empty}>
              <h2 className={css.emptyTitle}>No accounts connected</h2>
              <p className={css.emptyText}>Connect a social account to start scheduling posts.</p>
              <ActionButton variant="secondary" onClick={() => setConnectOpen(true)}>
                Connect an account
              </ActionButton>
            </div>
          )}
          {/* Keyed by month so per-month view state (the "+N more" popout) resets with it. */}
          {view === 'calendar' ? (
            <ScheduleCalendar key={gridKey} month={month} now={now} />
          ) : (
            <ScheduleAgenda key={gridKey} month={month} now={now} />
          )}
        </div>
      </div>
    </AppPage>
  );
});
